import { useEffect, useState, useCallback } from 'react'
import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  FileText,
  FileSpreadsheet,
  File,
  Search,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Document } from '../types'
import { formatDate } from '../utils/helpers'

type MediaItem = Document & { isImage: boolean }

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp']
const PAGE_SIZE = 20

const MODULE_COLORS: Record<string, string> = {
  societe: 'bg-gray-100 text-gray-600',
  invoice: 'bg-blue-100 text-blue-700',
  order: 'bg-purple-100 text-purple-700',
  shipment: 'bg-orange-100 text-orange-700',
  ticket: 'bg-red-100 text-red-700',
  proposal: 'bg-green-100 text-green-700',
  project: 'bg-indigo-100 text-indigo-700',
}

function getExt(type: string, name?: string): string {
  let ext = type.toLowerCase()
  if ((ext === 'file' || ext === '') && name) {
    ext = name.split('.').pop()?.toLowerCase() ?? ''
  }
  return ext
}

function isImageExt(ext: string): boolean {
  return IMAGE_EXTS.includes(ext)
}

function getDownloadUrl(fullPath: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || '/api'
  const token = localStorage.getItem('auth_token') ?? ''
  const tokenParam = token ? `&token=${encodeURIComponent(token)}` : ''
  return `${baseUrl}/documents/download?path=${encodeURIComponent(fullPath)}${tokenParam}`
}

function FileIcon({ ext, className }: { ext: string; className?: string }) {
  const cls = className ?? 'w-12 h-12'
  if (ext === 'pdf') return <FileText className={`${cls} text-red-500`} />
  if (['doc', 'docx'].includes(ext)) return <FileText className={`${cls} text-blue-500`} />
  if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className={`${cls} text-green-500`} />
  return <File className={`${cls} text-gray-400`} />
}

export default function Gallery() {
  const { t } = useTranslation()

  const getModuleLabel = (mod: string): string => {
    switch (mod) {
      case 'societe':
        return t('gallery.moduleSociete')
      case 'invoice':
        return t('gallery.moduleInvoice')
      case 'order':
        return t('gallery.moduleOrder')
      case 'shipment':
        return t('gallery.moduleShipment')
      case 'ticket':
        return t('gallery.moduleTicket')
      case 'proposal':
        return t('gallery.moduleProposal')
      case 'project':
        return t('gallery.moduleProject')
      default:
        return mod
    }
  }

  const [items, setItems] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [mediaFilter, setMediaFilter] = useState<'all' | 'images' | 'files'>('all')
  const [moduleFilter, setModuleFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  useEffect(() => {
    api
      .getGallery()
      .then((data) => {
        const docs: MediaItem[] = (Array.isArray(data) ? data : []).map((doc) => {
          const ext = getExt(doc.type, doc.name)
          return { ...doc, isImage: isImageExt(ext) }
        })
        setItems(docs)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [mediaFilter, moduleFilter, search])

  // Modules présents dans les données
  const availableModules = Array.from(
    new Set(items.map((i) => i.modulepart).filter(Boolean))
  ) as string[]

  const filtered = items.filter((item) => {
    if (mediaFilter === 'images' && !(item as MediaItem).isImage) return false
    if (mediaFilter === 'files' && (item as MediaItem).isImage) return false
    if (moduleFilter !== 'all' && item.modulepart !== moduleFilter) return false
    if (search && !item.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const visible = filtered.slice(0, visibleCount)
  const remaining = filtered.length - visibleCount

  const images = filtered.filter((i) => i.isImage)

  const prevImage = useCallback(() => {
    if (lightbox === null) return
    setLightbox((lightbox - 1 + images.length) % images.length)
  }, [lightbox, images.length])

  const nextImage = useCallback(() => {
    if (lightbox === null) return
    setLightbox((lightbox + 1) % images.length)
  }, [lightbox, images.length])

  useEffect(() => {
    if (lightbox === null) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevImage()
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'Escape') setLightbox(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [lightbox, prevImage, nextImage])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  const lightboxItem = lightbox !== null ? images[lightbox] : null

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 space-y-3">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Filtre média */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-sm font-medium">
            {(['all', 'images', 'files'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setMediaFilter(f)}
                className={`px-4 py-2 transition-colors ${mediaFilter === f ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {f === 'all'
                  ? t('gallery.filterAll')
                  : f === 'images'
                    ? t('gallery.filterImages')
                    : t('gallery.filterFiles')}
              </button>
            ))}
          </div>
          {/* Recherche */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('gallery.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
          <span className="text-sm text-gray-400">
            {t('gallery.items', { count: filtered.length })}
          </span>
        </div>

        {/* Filtre par module source */}
        {availableModules.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setModuleFilter('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${moduleFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {t('gallery.allModules')}
            </button>
            {availableModules.map((mod) => (
              <button
                key={mod}
                onClick={() => setModuleFilter(moduleFilter === mod ? 'all' : mod)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  moduleFilter === mod
                    ? 'bg-gray-800 text-white'
                    : `${MODULE_COLORS[mod] ?? 'bg-gray-100 text-gray-600'} hover:opacity-80`
                }`}
              >
                {getModuleLabel(mod)}
              </button>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100 text-gray-500">
          {t('gallery.notFound')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {visible.map((item, idx) => {
              const ext = getExt(item.type, item.name)
              const imgIdx = images.indexOf(item)
              const dlUrl = getDownloadUrl(item.full_path)
              const modpart = item.modulepart as string | undefined
              const entityRef = item.entity_ref as string | undefined
              return (
                <div
                  key={`${item.full_path}-${idx}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group flex flex-col"
                >
                  {/* Fixed 200px media area */}
                  <div
                    className={`relative h-[200px] flex-shrink-0 ${item.isImage ? 'cursor-zoom-in bg-gray-200 animate-pulse img-skeleton' : 'bg-gray-50'}`}
                    onClick={() => item.isImage && imgIdx >= 0 && setLightbox(imgIdx)}
                  >
                    {item.isImage ? (
                      <>
                        <img
                          src={dlUrl}
                          alt={item.name}
                          className="w-full h-full object-cover opacity-0 transition-[opacity,transform] duration-500 group-hover:scale-105"
                          loading="lazy"
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement
                            img.classList.remove('opacity-0')
                            img.classList.add('opacity-100')
                            img
                              .closest('.img-skeleton')
                              ?.classList.remove('animate-pulse', 'bg-gray-200')
                          }}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement
                            img.style.display = 'none'
                            img.closest('.img-skeleton')?.classList.remove('animate-pulse')
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center pointer-events-none">
                          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileIcon ext={ext} />
                      </div>
                    )}
                  </div>
                  <div className="p-2 space-y-1 flex-1">
                    <p className="text-xs font-medium text-gray-800 truncate" title={item.name}>
                      {item.name}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {modpart && (
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${MODULE_COLORS[modpart] ?? 'bg-gray-100 text-gray-600'}`}
                        >
                          {getModuleLabel(modpart)}
                        </span>
                      )}
                      {entityRef && (
                        <span className="text-xs text-gray-400 truncate">{entityRef}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{formatDate(item.date)}</p>
                  </div>
                  <a
                    href={dlUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center justify-center gap-1.5 py-1.5 text-xs text-gray-500 hover:text-primary-600 hover:bg-primary-50 border-t border-gray-100 transition-colors"
                  >
                    <Download className="w-3 h-3" /> {t('gallery.download')}
                  </a>
                </div>
              )
            })}
          </div>

          {/* Load more button */}
          {remaining > 0 && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                className="px-6 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
              >
                {t('gallery.loadMore', { remaining })}
              </button>
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightbox !== null && lightboxItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              prevImage()
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              nextImage()
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <div
            className="max-w-5xl max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getDownloadUrl(lightboxItem.full_path)}
              alt={lightboxItem.name}
              className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center text-white">
              <p className="font-medium">{lightboxItem.name}</p>
              {(() => {
                const mp = lightboxItem.modulepart as string | undefined
                const ref = lightboxItem.entity_ref as string | undefined
                return mp || ref ? (
                  <p className="text-sm text-gray-400 mt-0.5">
                    {mp ? getModuleLabel(mp) : ''}
                    {ref ? ` · ${ref}` : ''}
                  </p>
                ) : null
              })()}
              <p className="text-sm text-gray-400 mt-1">
                {lightbox + 1} / {images.length}
              </p>
            </div>
            <a
              href={getDownloadUrl(lightboxItem.full_path)}
              target="_blank"
              rel="noreferrer"
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg text-sm transition-colors"
            >
              <Download className="w-4 h-4" /> Télécharger
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
